/**
 * hooks/use-wishlist.ts
 *
 * TanStack Query hooks for the wishlist feature:
 * - useWishlist()            — GET /wishlist (all pending items for couple)
 * - useWishlistByVibe(vibe)  — GET /wishlist/:vibe (pending items for one vibe)
 * - useWishlistDone()        — GET /wishlist/done (completed items)
 * - useCreateWishlistItem()  — POST /wishlist
 * - useUpdateWishlistItem()  — PATCH /wishlist/:id
 * - useMarkWishlistDone()    — POST /wishlist/:id/done
 * - useNudgeWishlistItem()   — POST /wishlist/:id/nudge
 * - useDeleteWishlistItem()  — DELETE /wishlist/:id
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { WishlistItem, WishlistVibe } from '@just-us/shared'
import { apiFetch } from '../lib/api-client'

// ---------------------------------------------------------------------------
// API response shapes (match the actual Hono route return values)
// ---------------------------------------------------------------------------

interface WishlistResponse {
  data: {
    items: WishlistItem[]
    grouped: Record<string, WishlistItem[]>
    vibeCounts: Record<string, { total: number; pending: number; done: number }>
  }
}

interface WishlistDoneResponse {
  data: {
    items: WishlistItem[]
    grouped: Record<string, WishlistItem[]>
    total: number
  }
}

interface WishlistVibeResponse {
  data: WishlistItem[]
}

// ---------------------------------------------------------------------------
// Query keys
// ---------------------------------------------------------------------------

export const WISHLIST_QUERY_KEY = ['wishlist'] as const
export const WISHLIST_DONE_QUERY_KEY = ['wishlist', 'done'] as const
export const wishlistVibeKey = (vibe: WishlistVibe) => ['wishlist', vibe] as const

// ---------------------------------------------------------------------------
// useWishlist — all items (pending + done), full grouped response
// ---------------------------------------------------------------------------

export const useWishlist = () =>
  useQuery({
    queryKey: WISHLIST_QUERY_KEY,
    queryFn: () => apiFetch<WishlistResponse>('/wishlist'),
    staleTime: 30_000,
  })

// ---------------------------------------------------------------------------
// useWishlistByVibe — pending items for one vibe
// ---------------------------------------------------------------------------

export const useWishlistByVibe = (vibe: WishlistVibe) =>
  useQuery({
    queryKey: wishlistVibeKey(vibe),
    queryFn: () => apiFetch<WishlistVibeResponse>(`/wishlist/${vibe}`),
    staleTime: 30_000,
  })

// ---------------------------------------------------------------------------
// useWishlistDone — completed items archive
// ---------------------------------------------------------------------------

export const useWishlistDone = () =>
  useQuery({
    queryKey: WISHLIST_DONE_QUERY_KEY,
    queryFn: () => apiFetch<WishlistDoneResponse>('/wishlist/done'),
    staleTime: 60_000,
  })

// ---------------------------------------------------------------------------
// useCreateWishlistItem — POST /wishlist
// ---------------------------------------------------------------------------

export interface CreateWishlistInput {
  vibe: WishlistVibe
  title: string
  note?: string
  linkUrl?: string
}

export const useCreateWishlistItem = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (input: CreateWishlistInput) =>
      apiFetch<{ data: WishlistItem }>('/wishlist', {
        method: 'POST',
        body: JSON.stringify(input),
      }),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: WISHLIST_QUERY_KEY })
      queryClient.invalidateQueries({ queryKey: wishlistVibeKey(variables.vibe) })
    },
  })
}

// ---------------------------------------------------------------------------
// useUpdateWishlistItem — PATCH /wishlist/:id
// ---------------------------------------------------------------------------

export interface UpdateWishlistInput {
  id: string
  vibe: WishlistVibe
  title?: string
  note?: string | null
  linkUrl?: string | null
}

export const useUpdateWishlistItem = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, ...body }: UpdateWishlistInput) =>
      apiFetch<{ data: WishlistItem }>(`/wishlist/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: WISHLIST_QUERY_KEY })
      queryClient.invalidateQueries({ queryKey: wishlistVibeKey(variables.vibe) })
    },
  })
}

// ---------------------------------------------------------------------------
// useMarkWishlistDone — POST /wishlist/:id/done
// ---------------------------------------------------------------------------

export const useMarkWishlistDone = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id }: { id: string; vibe: WishlistVibe }) =>
      apiFetch<{ data: WishlistItem }>(`/wishlist/${id}/done`, { method: 'POST' }),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: WISHLIST_QUERY_KEY })
      queryClient.invalidateQueries({ queryKey: wishlistVibeKey(variables.vibe) })
      queryClient.invalidateQueries({ queryKey: WISHLIST_DONE_QUERY_KEY })
    },
  })
}

// ---------------------------------------------------------------------------
// useNudgeWishlistItem — POST /wishlist/:id/nudge
// ---------------------------------------------------------------------------

export const useNudgeWishlistItem = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id }: { id: string; vibe: WishlistVibe }) =>
      apiFetch<{ ok: boolean }>(`/wishlist/${id}/nudge`, { method: 'POST' }),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: wishlistVibeKey(variables.vibe) })
      queryClient.invalidateQueries({ queryKey: WISHLIST_QUERY_KEY })
    },
  })
}

// ---------------------------------------------------------------------------
// useDeleteWishlistItem — DELETE /wishlist/:id
// ---------------------------------------------------------------------------

export const useDeleteWishlistItem = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id }: { id: string; vibe: WishlistVibe }) =>
      apiFetch<{ ok: boolean }>(`/wishlist/${id}`, { method: 'DELETE' }),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: WISHLIST_QUERY_KEY })
      queryClient.invalidateQueries({ queryKey: wishlistVibeKey(variables.vibe) })
    },
  })
}
